import { useRouter, useSegments, type Href } from 'expo-router';
import { useEffect, useRef } from 'react';

import { ROUTES } from '@/constants/routes';
import { useInquiryClose } from '@/hooks/inbox/useInquiryClose';
import { useIsSmallDevice } from '@/hooks/useIsSmallDevice';

/**
 * Effect hook that handles post-close navigation.
 *
 * Call at the app/layout level so the effect fires reliably regardless
 * of which screen is active. Follows the state machine pattern:
 * 1. State machine, inquiryCloseAtom, manages transitions via actions
 * 2. This effect responds to state.status === 'complete'
 * 3. Navigation happens as a side effect of state transitions
 *
 * Must be called where useRouter() is available.
 *
 * The close flow can end on the feedback screens, which are stacked over the tabs, or on the
 * chat itself, inside the Inbox tab. From over the tabs the destination is opened inside the
 * tabs underneath rather than replacing the feedback screen, which would stack a second tab
 * navigator. The inbox is always reached by going back to it: replacing the chat with it inside
 * the Inbox tab would leave a second inbox above the first.
 */
export function useInquiryCloseNavigationEffect() {
  const { replace, dismissTo } = useRouter();

  const segments = useSegments();

  const isOverTabs = segments.at(1) !== '(tabs)';

  const isSmallDevice = useIsSmallDevice();

  const { state, isFeedbackActive, reset } = useInquiryClose();

  /**
   * Track last navigated conversation to prevent duplicate navigation.
   * Uses startedAt as part of the key to allow re-closing the same conversation
   * in a new close flow.
   */
  const lastNavigatedRef = useRef<{ conversationId: string | null; startedAt: number | null }>({
    conversationId: null,
    startedAt: null,
  });

  useEffect(() => {
    if (state.status !== 'complete') {
      return;
    }

    if (!state.navigationTarget) {
      return;
    }

    if (isFeedbackActive) {
      return;
    }

    // Prevent duplicate navigation for the same close flow
    const alreadyNavigated =
      lastNavigatedRef.current.conversationId === state.conversationId &&
      lastNavigatedRef.current.startedAt === state.startedAt;

    if (alreadyNavigated) {
      return;
    }

    lastNavigatedRef.current = {
      conversationId: state.conversationId,
      startedAt: state.startedAt,
    };

    const { shouldNavigate, nextConversationId } = state.navigationTarget;

    const go = (pathname: string, params: Record<string, string>) => {
      const href = { pathname, params } as Href;

      if (isOverTabs || pathname === ROUTES.INBOX) {
        dismissTo(href);

        return;
      }

      replace(href);
    };

    if (!shouldNavigate) {
      reset();

      return;
    }

    if (nextConversationId) {
      if (isSmallDevice) {
        go(ROUTES.INBOX_CHAT, { id: nextConversationId, tab: 'open' });
      } else {
        go(ROUTES.INBOX, { id: nextConversationId, tab: 'open' });
      }
    } else {
      go(ROUTES.INBOX, { tab: 'open' });
    }

    reset();
  }, [
    state.status,
    state.navigationTarget,
    state.conversationId,
    state.startedAt,
    isFeedbackActive,
    isSmallDevice,
    isOverTabs,
    replace,
    dismissTo,
    reset,
  ]);
}
